import React, { useEffect, useState } from "react";
import {
  Card, CardContent, Typography, Accordion, AccordionSummary, AccordionDetails,
  TextField, Button, Snackbar, Alert, Stack, Switch, FormControlLabel, LinearProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MDEditor from "@uiw/react-md-editor";
import { getFirestore, collection, getDocs, doc, updateDoc, setDoc, deleteDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

function CategoryNotesManager({ categoryId }) {
  const db = getFirestore();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, noteId: null });
  const [showArchived, setShowArchived] = useState(false);
  const [showPreview, setShowPreview] = useState(false);


  const notesRef = collection(db, `recipe_categories/${categoryId}/notes`);

  const fetchNotes = async () => {
    setLoading(true);
    const snapshot = await getDocs(notesRef);
    const data = snapshot.docs.map((doc, idx) => ({ id: doc.id, ...doc.data(), isNew: false }));
    setNotes(data.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    setLoading(false);
  };

  useEffect(() => {
    if (categoryId) fetchNotes();
  }, [categoryId]);

  const handleSave = async (id) => {
    const note = notes.find(n => n.id === id);
    const ref = doc(db, `recipe_categories/${categoryId}/notes`, id);
    await setDoc(ref, {
      ...note,
      updated_at: new Date().toISOString(),
      version: (note.version || 0) + 1,
      isNew: false
    });
    setNotes(prev => prev.map(n => n.id === id ? { ...n, version: (n.version || 0) + 1, updated_at: new Date().toISOString(), isNew: false } : n));
    setSnackbar({ open: true, message: "Note saved", severity: "success" });
  };

  const confirmAction = (action, noteId) => {
    setConfirmDialog({ open: true, action, noteId });
  };

  const handleConfirmedAction = async () => {
    const { action, noteId } = confirmDialog;
    const ref = doc(db, `recipe_categories/${categoryId}/notes`, noteId);
    if (action === "archive") {
      await updateDoc(ref, { archived: true });
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, archived: true } : n));
    } else if (action === "delete") {
      await deleteDoc(ref);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } else if (action === "restore") {
      await updateDoc(ref, { archived: false });
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, archived: false } : n));
    }
    setConfirmDialog({ open: false, action: null, noteId: null });
  };

  const handleAddNote = () => {
    const id = uuidv4();
    const newNote = {
      id,
      title: "New Note",
      content: "",
      updated_at: new Date().toISOString(),
      version: 1,
      archived: false,
      sort_order: notes.length,
      isNew: true
    };
    setNotes(prev => [...prev, newNote]);
    setExpandedNoteId(id);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = [...notes];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    const updates = reordered.map((n, idx) => ({ ...n, sort_order: idx }));
    setNotes(updates);
    for (const note of updates) {
      const ref = doc(db, `recipe_categories/${categoryId}/notes`, note.id);
      await updateDoc(ref, { sort_order: note.sort_order });
    }
  };

  const handleChange = (id, field, value) => {
    setNotes(prev => prev.map(n => n.id === id ? { ...n, [field]: value } : n));
  };

  const filtered = notes.filter(n => n.archived === showArchived);

  return (
    <Card>
      {loading && <LinearProgress />}
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">📝 Category Notes: {categoryId}</Typography>
          <Stack direction="row" spacing={2}>
            <FormControlLabel control={<Switch checked={showArchived} onChange={() => setShowArchived(!showArchived)} />} label="Show Archived" />
            <Button variant="contained" onClick={handleAddNote}>New Note</Button>
          </Stack>
        </Stack>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="notes">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {filtered.map((note, index) => (
                  <Draggable key={note.id} draggableId={note.id} index={index}>
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.draggableProps} style={{ ...provided.draggableProps.style }}>
                        <Accordion expanded={expandedNoteId === note.id} onChange={() => setExpandedNoteId(expandedNoteId === note.id ? null : note.id)}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} {...provided.dragHandleProps}>
                            <DragIndicatorIcon sx={{ mr: 1 }} />
                            <Typography>{note.title}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="caption">Version {note.version} | Last Updated: {new Date(note.updated_at).toLocaleString()}</Typography>
                            <TextField
                              label="Title"
                              fullWidth
                              margin="dense"
                              value={note.title}
                              onChange={(e) => handleChange(note.id, "title", e.target.value)}
                            />
                            <MDEditor
                                value={note.content}
                                onChange={(val) => handleChange(note.id, "content", val)}
                                height={200}
                                preview={note.archived || showPreview ? "preview" : "edit"} // 👈 disables split screen
                                hideToolbar={false} // optional
                            />
                            <Stack direction="row" spacing={2} mt={2}>
                              {!note.archived ? (
                                <>
                                  <Button variant="contained" onClick={() => handleSave(note.id)}>Save</Button>
                                  <Button color="warning" onClick={() => confirmAction("archive", note.id)}>Archive</Button>
                                </>
                              ) : (
                                <>
                                  <Button variant="contained" onClick={() => confirmAction("restore", note.id)}>Restore</Button>
                                  <Button color="error" onClick={() => confirmAction("delete", note.id)}>Delete</Button>
                                </>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>

        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, noteId: null })}>
          <DialogTitle>Confirm</DialogTitle>
          <DialogContentText>
            {(() => {
                const noteTitle = notes.find(n => n.id === confirmDialog.noteId)?.title || "Untitled";
                if (confirmDialog.action === "delete") return `Delete the note: "${noteTitle}" permanently? This cannot be undone.`;
                if (confirmDialog.action === "archive") return `Archive the note: "${noteTitle}"?`;
                if (confirmDialog.action === "restore") return `Restore the note: "${noteTitle}"?`;
                return "";
            })()}
            </DialogContentText>

          <DialogActions>
            <Button onClick={() => setConfirmDialog({ open: false, action: null, noteId: null })}>Cancel</Button>
        <Button onClick={handleConfirmedAction} color={confirmDialog.action === "delete" ? "error" : "primary"}>
              {confirmDialog.action === "delete" ? "Delete" : confirmDialog.action === "archive" ? "Archive" : "Restore"}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default CategoryNotesManager;
